import { useEffect, useState } from 'react'
import type { TelegramWebApp } from '@shared/types/telegram'

export function useTelegramWebApp() {
  const [telegram] = useState<TelegramWebApp | undefined>(() => window.Telegram?.WebApp)

  useEffect(() => {
    if (!telegram) return

    // Инициализация Telegram WebApp
    telegram.ready()
    telegram.expand()

    // Запрещаем закрытие свайпом вниз
    if (telegram.disableVerticalSwipes) {
      telegram.disableVerticalSwipes()
    }

    // Применяем тему Telegram
    if (telegram.themeParams.bg_color) {
      document.body.style.backgroundColor = telegram.themeParams.bg_color
    }

    // Устанавливаем CSS переменные для Telegram theme
    const root = document.documentElement
    Object.entries(telegram.themeParams).forEach(([key, value]) => {
      if (value) {
        root.style.setProperty(`--tg-theme-${key.replace(/_/g, '-')}`, value)
      }
    })
  }, [telegram])

  return {
    telegram,
    user: telegram?.initDataUnsafe?.user,
    themeParams: telegram?.themeParams,
    platform: telegram?.platform,
    colorScheme: telegram?.colorScheme,
  }
}

// Hook для работы с MainButton
export function useMainButton(text: string, onClick: () => void, show = true) {
  const { telegram } = useTelegramWebApp()

  useEffect(() => {
    if (!telegram?.MainButton) return

    telegram.MainButton.setText(text)
    telegram.MainButton.onClick(onClick)

    if (show) {
      telegram.MainButton.show()
    } else {
      telegram.MainButton.hide()
    }

    return () => {
      telegram.MainButton.offClick(onClick)
      telegram.MainButton.hide()
    }
  }, [telegram, text, onClick, show])
}

// Hook для работы с BackButton
export function useBackButton(onClick: () => void, show = true) {
  const { telegram } = useTelegramWebApp()

  useEffect(() => {
    if (!telegram?.BackButton) return

    telegram.BackButton.onClick(onClick)

    if (show) {
      telegram.BackButton.show()
    } else {
      telegram.BackButton.hide()
    }

    return () => {
      telegram.BackButton.offClick(onClick)
      telegram.BackButton.hide()
    }
  }, [telegram, onClick, show])
}

// Hook для Haptic Feedback
export function useHapticFeedback() {
  const { telegram } = useTelegramWebApp()

  return {
    impact: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') => {
      telegram?.HapticFeedback.impactOccurred(style)
    },
    notification: (type: 'error' | 'success' | 'warning') => {
      telegram?.HapticFeedback.notificationOccurred(type)
    },
    selectionChanged: () => {
      telegram?.HapticFeedback.selectionChanged()
    },
  }
}
